import React, { useState, useEffect } from 'react';
import ColorBends from './components/ColorBends';
import GradualBlur from '@/components/GradualBlur';
import ScrollIndicator from '@/components/ScrollIndicator';
import AdvancedMorphingText, { ProfileText } from '@/components/AdvancedMorphingText';
import GlowCard from '@/components/GlowCard';
import MagneticLink from '@/components/MagneticLink';
import Reveal from '@/components/Reveal';
import CreditTags, { Credit } from '@/components/CreditTags';
import { Youtube, PlayCircle, Instagram, Disc3, ListMusic } from 'lucide-react';
import { motion, useMotionValue, useScroll, useSpring } from 'framer-motion';

// Artists in the credits line. Give an artist a `profiles` list and their tag
// becomes clickable — it opens a menu of their links. Leave `profiles` off (or
// empty) and the tag stays plain text.
//
// Available platforms: instagram | spotify | appleMusic | youtube | soundcloud
//                      tiktok | beatstars | website
// Each entry takes { platform, href } plus an optional `label` to override the
// button text. Add, remove and reorder freely.
const CREDITS: Credit[] = [
  {
    name: 'Paintz',
    profiles: [
      {platform: 'instagram', href: 'https://www.instagram.com/paintzdnz'},
      {platform: 'spotify', href: 'https://open.spotify.com/artist/03crqRplVMJhdHsOilvOX3?si=GJOyFIwGQq2xEiQsAG6XOw'},
    ],
  },
  {
    name: 'Tenoo',
    profiles: [
      {platform: 'instagram', href: 'https://www.instagram.com/tenoo.pr'},
      {platform: 'spotify', href: 'https://open.spotify.com/artist/41rq03zBXAeG6smuDiRHC2?si=lApIbSm-QHqtEiQQ2E0ivA'},

    ],
  },
  {
    name: 'Dee L',
    profiles: [
      {platform: 'instagram', href: 'https://www.instagram.com/deelfromde'},
      {platform: 'spotify', href: 'https://open.spotify.com/artist/7DXKlFKSjotJWRlzaMISCJ?si=oiqinxyOQdifmItzQAIu8A'},
    ],
  },
  {
    name: 'Lee Flex',
    profiles: [
      {platform: 'instagram', href: 'https://www.instagram.com/leeflexstar'},
      {platform: 'spotify', href: 'https://open.spotify.com/artist/3IbCi3T8RFSZt4iFUzmZqq?si=514Pls40QrKmpl0DyM14ag'},
    ],
  }
];

const LINKS = [
  {
    href: 'https://www.youtube.com/@archxve2-lv2eg',
    label: 'Remixes',
    meta: 'YouTube · @archxve2',
    icon: <Youtube className="h-5 w-5" />,
    featured: true,
  },
  {
    href: 'https://www.youtube.com/@prodarchxve',
    label: 'UK Beats',
    meta: 'YouTube · @prodarchxve',
    icon: <Youtube className="h-5 w-5" />,
    featured: false,
  },
  {
    href: 'https://www.instagram.com/archxvemusic',
    label: 'Instagram',
    meta: '@archxvemusic',
    icon: <Instagram className="h-5 w-5" />,
    featured: false,
  },
  {
    href: 'https://www.beatstars.com/archxvemusic',
    label: 'Beatstars',
    meta: 'Buy beats · archxvemusic',
    icon: <span className="text-base font-black leading-none">B</span>,
    featured: true,
  },
];

function App() {
  const [scrollY, setScrollY] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const motionScrollY = useMotionValue(0);

  // Top-of-page reading progress bar
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      setScrollY(currentScroll);
      motionScrollY.set(currentScroll);
      if (currentScroll > 50 && !hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled, motionScrollY]);

  // Calculate progress based on scroll
  const maxScroll = 400;
  const scrollProgress = Math.min(scrollY / maxScroll, 1);

  return (
    <div className="relative min-h-[200vh] overflow-x-hidden bg-black">
      {/* Scroll progress bar */}
      <motion.div
        aria-hidden
        style={{ scaleX: progress }}
        className="fixed inset-x-0 top-0 z-50 h-[2px] origin-left bg-gradient-to-r from-red-700 via-red-500 to-red-300 shadow-[0_0_12px_rgba(239,68,68,0.8)]"
      />

      {/* Fixed Background */}
      <div className="grain-overlay fixed inset-0 overflow-hidden bg-black">
        {/* ColorBends Three.js shader background */}
        <div className="absolute inset-0 opacity-95">
          <ColorBends />
        </div>

        {/* Dark red overlay for sinister mood */}
        <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-black/50 to-black/70" />

        {/* Vignette to pull the eye toward the centre column */}
        <div className="vignette absolute inset-0" />

        {/* Gradual Blur Effect during scroll */}
        {scrollY > 20 && (
          <GradualBlur
            position="top"
            strength={3}
            height="30vh"
            animated="scroll"
            curve="ease-out"
            opacity={Math.min(scrollProgress * 1.5, 1)}
          />
        )}
      </div>

      {/* Hero Section - Large Centered Title with Advanced Morphing Effect */}
      <div className="pointer-events-none fixed inset-0">
        <AdvancedMorphingText scrollY={motionScrollY} />
      </div>

      {/* Scroll Down Indicator - Shows when at top */}
      {scrollY < 50 && <ScrollIndicator className="animate-in fade-in delay-500 duration-1000" />}

      {/* Scroll Content */}
      <div className="relative pt-[100vh]">
        <div className="relative z-10 mx-auto max-w-md space-y-10 px-5 pb-24">
          {/* Profile Section */}
          <div className="space-y-6 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, filter: 'blur(14px)' }}
              whileInView={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="group relative mx-auto h-36 w-36"
            >
              {/* Pulsing halo */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full border border-red-500/40 animate-pulse-ring"
              />
              {/* Soft bloom behind the avatar */}
              <span
                aria-hidden
                className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-400 opacity-40 blur-2xl transition-opacity duration-700 group-hover:opacity-80"
              />
              {/* Rotating conic ring */}
              <span
                aria-hidden
                className="absolute -inset-[3px] rounded-full opacity-70 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                  padding: '2px',
                  background:
                    'conic-gradient(from 0deg, transparent 0deg, rgba(248,113,113,0.9) 60deg, transparent 140deg, transparent 220deg, rgba(220,38,38,0.8) 300deg, transparent 360deg)',
                  WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  animation: 'ring-spin 8s linear infinite',
                }}
              />
              <div className="relative h-full w-full overflow-hidden rounded-full border border-white/15 shadow-2xl shadow-red-900/50 transition-transform duration-500 ease-out group-hover:scale-[1.04]">
                <img
                  src="/archxve-pfp.png"
                  alt="ARCHXVE"
                  className="h-full w-full scale-105 object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                {/* Glass sheen across the avatar on hover */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                />
              </div>
            </motion.div>

            <div className="min-h-[120px] space-y-3">
              <ProfileText />

              <Reveal delay={0.05}>
                <div className="flex items-center justify-center gap-3">
                  <span className="h-px w-8 bg-gradient-to-r from-transparent to-red-500/60" />
                  <p className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-red-400">
                    Multi-Genre Beatmaker
                  </p>
                  <span className="h-px w-8 bg-gradient-to-l from-transparent to-red-500/60" />
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <CreditTags credits={CREDITS} />
              </Reveal>
            </div>
          </div>

          <div className="hairline" />

          {/* Featured Release */}
          <GlowCard
            eyebrow="Now Playing"
            title="Latest Credits"
            icon={<Disc3 className="h-5 w-5" />}
          >
            <iframe
              data-testid="embed-iframe"
              style={{ borderRadius: '12px' }}
              src="https://open.spotify.com/embed/track/5n8DOGZHdeoyHI0llpja1S?utm_source=generator&theme=0"
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Latest Credits"
            />
          </GlowCard>

          {/* Latest Remix */}
          <GlowCard
            eyebrow="Fresh Drop"
            title="New Remix"
            icon={<PlayCircle className="h-5 w-5" />}
            delay={0.05}
          >
            <div className="relative w-full overflow-hidden rounded-xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute left-0 top-0 h-full w-full rounded-xl"
                src="https://www.youtube.com/embed/dPG9jsEbGDs"
                title="ARCHXVE — Latest Remix"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </GlowCard>

          {/* Placements Playlist */}
          <GlowCard
            eyebrow="Discography"
            title="Placements"
            icon={<ListMusic className="h-5 w-5" />}
            delay={0.05}
          >
            <iframe
              data-testid="embed-iframe"
              style={{ borderRadius: '12px' }}
              src="https://open.spotify.com/embed/playlist/6EzpAZGoyMfQIuqSIcNTPz?utm_source=generator&theme=0"
              width="100%"
              height="352"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Spotify Playlist"
            />
          </GlowCard>

          <div className="hairline" />

          {/* Links */}
          <div className="space-y-4">
            <Reveal y={16}>
              <p className="text-center text-[0.62rem] font-semibold uppercase tracking-[0.3em] text-white/30">
                Everywhere Else
              </p>
            </Reveal>

            <div className="space-y-3">
              {LINKS.map((link, i) => (
                <MagneticLink
                  key={link.href}
                  href={link.href}
                  label={link.label}
                  meta={link.meta}
                  icon={link.icon}
                  featured={link.featured}
                  delay={i * 0.07}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          <Reveal y={12} delay={0.1}>
            <div className="space-y-3 pt-4 text-center">
              <div className="hairline opacity-50" />
              <p className="text-xs tracking-wide text-white/25">
                © 2026 <span className="font-semibold text-white/40">ARCHXVE</span>. All rights reserved.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

export default App;
